import React from 'react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { useTheme } from '@/hooks/useTheme';

interface LineChartProps {
  data: any[];
  xKey: string;
  lines: {
    dataKey: string;
    name: string;
    color: string;
    strokeWidth?: number;
    strokeDasharray?: string;
  }[];
  height?: number;
  className?: string;
  formatTooltip?: (value: any, name: string) => [string, string];
  formatXAxisLabel?: (value: any) => string;
  formatYAxisLabel?: (value: any) => string;
}

export const LineChart: React.FC<LineChartProps> = ({
  data,
  xKey,
  lines,
  height = 300,
  className = '',
  formatTooltip,
  formatXAxisLabel,
  formatYAxisLabel
}) => {
  const { isDark } = useTheme();

  // Dark mode colors
  const gridColor = isDark ? 'rgba(255, 255, 255, 0.06)' : '#f0f0f0';
  const axisColor = isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb';
  const tickColor = isDark ? '#71717a' : '#6b7280';
  const tooltipBg = isDark ? '#18181b' : 'white';
  const tooltipBorder = isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb';
  const tooltipLabelColor = isDark ? '#f4f4f5' : '#374151';
  const defaultTooltipFormatter = (value: any, name: string) => {
    if (name.toLowerCase().includes('revenue') || name.toLowerCase().includes('$')) {
      return [`$${Number(value).toLocaleString()}`, name];
    }
    return [Number(value).toLocaleString(), name];
  };

  const defaultXAxisFormatter = (value: any) => {
    if (typeof value === 'string' && value.includes('-')) {
      // Handle YYYY-MM format
      const [year, month] = value.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }
    return String(value);
  };

  const defaultYAxisFormatter = (value: any) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return String(value);
  };

  return (
    <div className={`w-full ${className}`}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsLineChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            dataKey={xKey}
            tick={{ fontSize: 12, fill: tickColor }}
            tickFormatter={formatXAxisLabel || defaultXAxisFormatter}
            stroke={axisColor}
          />
          <YAxis
            tick={{ fontSize: 12, fill: tickColor }}
            tickFormatter={formatYAxisLabel || defaultYAxisFormatter}
            stroke={axisColor}
          />
          <Tooltip
            formatter={formatTooltip || defaultTooltipFormatter}
            labelStyle={{ color: tooltipLabelColor, fontWeight: 'medium' }}
            contentStyle={{
              backgroundColor: tooltipBg,
              border: `1px solid ${tooltipBorder}`,
              borderRadius: '8px',
              boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
          />
          {lines.length > 1 && <Legend />}
          {lines.map((line) => (
            <Line
              key={line.dataKey}
              type="monotone"
              dataKey={line.dataKey}
              stroke={line.color}
              strokeWidth={line.strokeWidth || 2}
              strokeDasharray={line.strokeDasharray}
              name={line.name}
              dot={false}
              activeDot={{ r: 6, stroke: line.color, strokeWidth: 2 }}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
}; 